// Postgres rows <-> domain objects.
//
// Explicit field maps, deliberately, instead of a generic snake<->camel
// converter. Two reasons a converter breaks here:
//
//  1. Several renames are NOT mechanical. plans.usage_limit is `limit` in the
//     domain (entitlements.js reads plan.limit), class_templates.start_time is
//     `time`, invoices.failed_on is `failedOnKey`. A converter produces
//     usageLimit / startTime / failedOn and every rule silently reads
//     undefined — which, for a usage cap, means "unlimited".
//  2. jsonb columns (members.custom_fields, members.emergency_contact) carry
//     user-defined keys. A recursive converter rewrites the customer's own data.
//
// Every map below is a plain [column, field] list. jsonb columns are copied by
// reference and never walked.

const TO_DOMAIN = Symbol('toDomain');
const TO_ROW = Symbol('toRow');

/**
 * Build a bidirectional mapper from a column->field spec.
 *
 * `jsonb` lists columns whose values are opaque user data. It is documentation
 * plus a dev-time guard, not transformation logic — values are copied by
 * reference here because this mapper never walks into a value at all, which is
 * precisely what keeps customer-defined keys intact.
 */
function mapper(spec, { jsonb = [] } = {}) {
  const pairs = Object.entries(spec);
  const opaque = new Set(jsonb.map((col) => spec[col]));

  return {
    opaqueFields: opaque,
    [TO_DOMAIN](row) {
      if (!row) return null;
      const out = {};
      for (const [col, field] of pairs) {
        if (!(col in row)) continue;
        out[field] = row[col];
      }
      return out;
    },
    [TO_ROW](obj) {
      if (!obj) return null;
      const out = {};
      for (const [col, field] of pairs) {
        if (!(field in obj)) continue;
        if (obj[field] === undefined) continue;
        out[col] = obj[field];
      }
      return out;
    },
  };
}

export const toDomain = (m, row) => m[TO_DOMAIN](row);
export const toRow = (m, obj) => m[TO_ROW](obj);
export const allToDomain = (m, rows) => (rows || []).map((r) => m[TO_DOMAIN](r));

/* ------------------------------------------------------------------ maps */

export const orgMap = mapper({
  id: 'id',
  name: 'name',
  time_zone: 'timeZone',
  currency: 'currency',
  tax_rate_bps: 'taxRateBps',
});

export const locationMap = mapper({
  id: 'id', org_id: 'orgId', name: 'name', time_zone: 'timeZone',
});

export const staffMap = mapper({
  id: 'id', org_id: 'orgId', user_id: 'userId', name: 'name',
  email: 'email', role: 'role', active: 'active',
});

export const memberMap = mapper({
  id: 'id',
  org_id: 'orgId',
  location_id: 'locationId',
  user_id: 'userId',
  name: 'name',
  email: 'email',
  phone: 'phone',
  status: 'status',
  joined_on: 'joinedOn',
  waiver_signed_at: 'waiverSignedAt',
  emergency_contact: 'emergencyContact',
  health_notes: 'healthNotes',
  custom_fields: 'customFields',
  notes: 'notes',
}, { jsonb: ['emergency_contact', 'custom_fields'] });

export const planMap = mapper({
  id: 'id',
  org_id: 'orgId',
  name: 'name',
  kind: 'kind',
  amount_cents: 'amountCents',
  currency: 'currency',
  interval: 'interval',
  // NOT usageLimit — entitlements.js reads plan.limit.
  usage_limit: 'limit',
  limit_period: 'limitPeriod',
  credits: 'credits',
  expiry_days: 'expiryDays',
  programs: 'programs',
  block_when_past_due: 'blockWhenPastDue',
  active: 'active',
});

export const membershipMap = mapper({
  id: 'id',
  org_id: 'orgId',
  member_id: 'memberId',
  plan_id: 'planId',
  status: 'status',
  start_date: 'startDate',
  end_date: 'endDate',
  current_period_start: 'currentPeriodStart',
  current_period_end: 'currentPeriodEnd',
  interval: 'interval',
  amount_cents: 'amountCents',
  credits_remaining: 'creditsRemaining',
  credits_expire_on: 'creditsExpireOn',
  past_due_cents: 'pastDueCents',
  cancelled_on: 'cancelledOn',
});

export const freezeMap = mapper({
  id: 'id',
  org_id: 'orgId',
  membership_id: 'membershipId',
  // The domain shape is {from, to} — entitlements.isFrozenOn reads exactly those.
  from_date: 'from',
  to_date: 'to',
  fee_cents: 'feeCents',
  reason: 'reason',
});

export const roomMap = mapper({
  id: 'id',
  org_id: 'orgId',
  location_id: 'locationId',
  name: 'name',
  capacity: 'capacity',
});

export const templateMap = mapper({
  id: 'id',
  org_id: 'orgId',
  location_id: 'locationId',
  name: 'name',
  program: 'program',
  weekdays: 'weekdays',
  // NOT startTime — recurrence.js reads template.time.
  start_time: 'time',
  duration_min: 'durationMin',
  capacity: 'capacity',
  start_date: 'startDate',
  end_date: 'endDate',
  time_zone: 'timeZone',
  staff_id: 'staffId',
  room_id: 'roomId',
  active: 'active',
});

export const overrideMap = mapper({
  id: 'id',
  org_id: 'orgId',
  template_id: 'templateId',
  on_date: 'date',
  cancelled: 'cancelled',
  name: 'name',
  // NOT startTime — recurrence.js reads the patch's `time`, same as a template.
  start_time: 'time',
  duration_min: 'durationMin',
  capacity: 'capacity',
  staff_id: 'staffId',
  room_id: 'roomId',
});

/**
 * Fold class_overrides rows into the templates they patch.
 *
 * recurrence.expandTemplate wants `exceptions` (dates that do not happen) and
 * `overrides` (a patch per date) inline on the template. They are rows in
 * Postgres so a single edited Tuesday does not require materialising a year of
 * classes.
 *
 * A key whose value is null is DROPPED rather than kept: expandTemplate reads
 * `patch.staffId || template.staffId`, so a null left in the patch would still
 * fall back correctly, but a null capacity would win over the template's via
 * `??` and produce a class with no seats.
 */
export function attachOverrides(templates, overrideRows) {
  const byTemplate = new Map();
  for (const row of allToDomain(overrideMap, overrideRows)) {
    if (!byTemplate.has(row.templateId)) {
      byTemplate.set(row.templateId, { exceptions: [], overrides: {} });
    }
    const bucket = byTemplate.get(row.templateId);
    if (row.cancelled) {
      bucket.exceptions.push(row.date);
      continue;
    }
    const patch = {};
    for (const key of ['name', 'time', 'durationMin', 'capacity', 'staffId', 'roomId']) {
      if (row[key] !== null && row[key] !== undefined) patch[key] = row[key];
    }
    if (Object.keys(patch).length > 0) bucket.overrides[row.date] = patch;
  }

  return templates.map((t) => ({
    ...t,
    exceptions: byTemplate.get(t.id)?.exceptions ?? [],
    overrides: byTemplate.get(t.id)?.overrides ?? {},
  }));
}

export const invoiceMap = mapper({
  id: 'id',
  org_id: 'orgId',
  member_id: 'memberId',
  membership_id: 'membershipId',
  status: 'status',
  currency: 'currency',
  subtotal_cents: 'subtotalCents',
  discount_cents: 'discountCents',
  tax_cents: 'taxCents',
  total_cents: 'totalCents',
  issued_on: 'issuedOn',
  due_on: 'dueOn',
  paid_on: 'paidOn',
  // NOT failedOn — billing.nextDunningStep takes failedOnKey.
  failed_on: 'failedOnKey',
  attempts_made: 'attemptsMade',
  last_attempt_on: 'lastAttemptOn',
});

export const policyMap = mapper({
  org_id: 'orgId',
  opens_hours_before: 'opensHoursBefore',
  closes_minutes_before: 'closesMinutesBefore',
  waitlist: 'waitlist',
  waitlist_cap: 'waitlistCap',
  late_cancel_minutes: 'lateCancelMinutes',
  late_cancel_fee_cents: 'lateCancelFeeCents',
  no_show_fee_cents: 'noShowFeeCents',
  late_cancel_returns_credit: 'lateCancelReturnsCredit',
});

/* ------------------------------------------------------- bookings + ids */

// occurrenceId is a DERIVED client key: `${templateId}:${YYYY-MM-DD}`. It is
// never persisted. The database stores template_id and on_date as columns,
// which is what makes a date-range query indexable.

export function occurrenceKey(templateId, onDate) {
  return `${templateId}:${onDate}`;
}

export function splitOccurrenceKey(key) {
  const at = key.lastIndexOf(':');
  if (at < 0) throw new Error(`malformed occurrence key: ${key}`);
  return { templateId: key.slice(0, at), onDate: key.slice(at + 1) };
}

const bookingCore = mapper({
  id: 'id',
  org_id: 'orgId',
  member_id: 'memberId',
  membership_id: 'membershipId',
  status: 'status',
  waitlist_position: 'waitlistPosition',
  consumed_credit: 'consumedCredit',
  consumed_usage: 'consumedUsage',
  fee_cents: 'feeCents',
  booked_at: 'bookedAt',
  checked_in_at: 'checkedInAt',
  cancelled_at: 'cancelledAt',
  promoted_at: 'promotedAt',
});

export function bookingToDomain(row) {
  if (!row) return null;
  return {
    ...toDomain(bookingCore, row),
    occurrenceId: occurrenceKey(row.template_id, row.on_date),
  };
}

export function bookingToRow(booking) {
  const row = toRow(bookingCore, booking);
  if (booking.occurrenceId) {
    const { templateId, onDate } = splitOccurrenceKey(booking.occurrenceId);
    row.template_id = templateId;
    row.on_date = onDate;
  }
  return row;
}

/* ----------------------------------------------------------- assembly */

/**
 * Attach freeze rows to their memberships as the {from,to} array the domain
 * expects. Freezes are rows in Postgres so history survives, but
 * entitlements.isFrozenOn wants them inline.
 */
export function attachFreezes(memberships, freezeRows) {
  const byMembership = new Map();
  for (const f of allToDomain(freezeMap, freezeRows)) {
    if (!byMembership.has(f.membershipId)) byMembership.set(f.membershipId, []);
    byMembership.get(f.membershipId).push({ from: f.from, to: f.to ?? null });
  }
  return memberships.map((m) => ({ ...m, freezes: byMembership.get(m.id) || [] }));
}

/**
 * Derive each membership's usageDates from bookings.
 *
 * Deliberately keyed on consumed_usage, NOT on status. Whether a late cancel
 * still burns the week's allowance is a studio policy decision made at write
 * time; inferring it from status here would silently re-decide it, and would
 * diverge from the semantics entitlements.usageInWindow is tested against.
 */
export function attachUsageDates(memberships, bookingRows) {
  const byMembership = new Map();
  for (const row of bookingRows || []) {
    if (!row.consumed_usage) continue;
    const key = row.membership_id;
    if (!key) continue;
    if (!byMembership.has(key)) byMembership.set(key, []);
    byMembership.get(key).push(row.on_date);
  }
  return memberships.map((m) => ({
    ...m,
    usageDates: (byMembership.get(m.id) || []).sort(),
  }));
}
